// SS3 Financial Accounting — Complete NERDC Curriculum Quiz Data
// First Term only (Second Term and Third Term not present in NERDC scheme for SS3 Financial Accounting)
// 7 topics x 20 questions = 140 questions

export const WAEC_ACCOUNTING_LEARN_SS3 = [
  {
    "topic": "Consignment Account – Meaning and Features",
    "topicCode": "SS3-ACC-01",
    "module": "Consignment Accounts",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">consignment</span> is an arrangement in which goods are sent by their owner to an agent in another location for the purpose of sale, while ownership of the goods remains with the sender until they are actually sold.\n</div>\n\n<h3 class=\"learn-subheading\">1. Parties Involved</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Party</th><th>Role</th></tr></thead>\n    <tbody>\n      <tr><td>Consignor</td><td>The owner of the goods who sends them out for sale; remains the legal owner until sold</td></tr>\n      <tr><td>Consignee</td><td>The agent who receives the goods and sells them on behalf of the consignor, in exchange for a commission</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Features of Consignment</h3>\n<ul class=\"learn-list\">\n  <li>Ownership of goods remains with the consignor until sold by the consignee</li>\n  <li>The consignee acts as an agent, not a buyer, and is not liable for unsold goods</li>\n  <li>The consignee earns a commission on sales made, not a profit margin like an owner would</li>\n  <li>Any loss or profit on eventual sale belongs to the consignor, not the consignee</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Key Documents</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Document</th><th>Purpose</th></tr></thead>\n    <tbody>\n      <tr><td>Proforma invoice</td><td>Sent by the consignor to the consignee listing the goods dispatched and their value; it is NOT a demand for payment, since the consignee has not bought the goods</td></tr>\n      <tr><td>Account sales</td><td>Sent by the consignee to the consignor, showing goods sold, expenses incurred, commission earned, and the net amount due to the consignor</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Consignment vs. Sale</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Consignment</th><th>Sale</th></tr></thead>\n    <tbody>\n      <tr><td>Ownership remains with the consignor until sold</td><td>Ownership transfers to the buyer immediately</td></tr>\n      <tr><td>Consignee is an agent</td><td>Buyer is an independent party</td></tr>\n      <tr><td>Consignee bears no risk of loss on unsold goods</td><td>Buyer bears full risk once purchased</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">5. Terms Used in Consignment</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Meaning</th></tr></thead>\n    <tbody>\n      <tr><td>Del credere commission</td><td>An extra commission paid to the consignee for taking on the risk of bad debts from credit sales</td></tr>\n      <tr><td>Ordinary commission</td><td>The normal commission paid to the consignee for selling the goods</td></tr>\n      <tr><td>Normal loss</td><td>An expected, unavoidable loss (e.g., evaporation, natural wastage) that is absorbed into the cost of remaining stock</td></tr>\n      <tr><td>Abnormal loss</td><td>An unexpected loss (e.g., due to fire, theft, or accident) that is charged directly to the profit and loss account, not spread over remaining stock</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Never confuse a proforma invoice with a normal sales invoice — a proforma invoice does not create a debtor-creditor relationship because ownership has not transferred.\n</div>\n",
    "questions": [
      {
        "q": "A 'consignment' is best defined as:",
        "o": [
          "A permanent sale of goods to another business",
          "An arrangement where goods are sent by their owner to an agent for sale, with ownership remaining with the sender until sold",
          "A loan given between two businesses",
          "A partnership agreement between two owners"
        ],
        "a": 1,
        "e": "A consignment is an arrangement where goods are sent to an agent for sale, while ownership remains with the original owner (consignor) until the goods are actually sold.",
        "h": "Focus on who retains ownership in this arrangement.",
        "yr": "SS3"
      },
      {
        "q": "The 'consignor' in a consignment arrangement is:",
        "o": [
          "The agent who sells the goods",
          "The owner of the goods who sends them out for sale",
          "A customer who buys the goods",
          "A bank providing credit"
        ],
        "a": 1,
        "e": "The consignor is the owner of the goods, who sends them to an agent (the consignee) for sale, while retaining ownership until sold.",
        "h": "Think of 'consign-OR' as the one who sends.",
        "yr": "SS3"
      },
      {
        "q": "The 'consignee' in a consignment arrangement is:",
        "o": [
          "The owner of the goods",
          "The agent who receives and sells the goods on behalf of the owner, earning a commission",
          "A government tax authority",
          "A shareholder in the business"
        ],
        "a": 1,
        "e": "The consignee is the agent who receives the goods and sells them on behalf of the consignor, earning a commission for this service.",
        "h": "Think of 'consign-EE' as the one who receives.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a key feature of a consignment arrangement?",
        "o": [
          "The consignee immediately owns the goods once received",
          "Ownership of goods remains with the consignor until the goods are sold",
          "The consignee bears the risk of unsold goods as an owner would",
          "The consignee earns a profit margin, not a commission"
        ],
        "a": 1,
        "e": "A defining feature of consignment is that ownership of the goods remains with the consignor until the goods are actually sold by the consignee.",
        "h": "Recall the first feature listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "A 'proforma invoice' in consignment accounting is:",
        "o": [
          "A demand for immediate payment from the consignee",
          "A document sent by the consignor listing goods dispatched and their value, not a demand for payment",
          "A document proving the consignee owns the goods",
          "A tax receipt"
        ],
        "a": 1,
        "e": "A proforma invoice lists the goods dispatched and their value but is not a demand for payment, since ownership has not transferred to the consignee.",
        "h": "Recall the exam tip about proforma invoices.",
        "yr": "SS3"
      },
      {
        "q": "An 'account sales' document is sent by:",
        "o": [
          "The consignor to the consignee",
          "The consignee to the consignor, showing goods sold, expenses, commission, and amount due",
          "A bank to the consignor",
          "A government agency to the consignee"
        ],
        "a": 1,
        "e": "An account sales document is sent by the consignee to the consignor, detailing goods sold, expenses incurred, commission earned, and the net amount due.",
        "h": "Think about which party reports back on the sales made.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following distinguishes a consignment from an ordinary sale?",
        "o": [
          "In a sale, ownership transfers immediately to the buyer; in a consignment, ownership remains with the consignor until sold",
          "They are exactly the same transaction",
          "In a consignment, the buyer bears all risk immediately",
          "A sale always involves an agent, while consignment does not"
        ],
        "a": 0,
        "e": "In an ordinary sale, ownership transfers immediately to the buyer, whereas in a consignment, ownership remains with the consignor until the goods are eventually sold by the consignee.",
        "h": "Focus on when ownership transfers in each type of transaction.",
        "yr": "SS3"
      },
      {
        "q": "'Del credere commission' is best described as:",
        "o": [
          "The normal commission for selling goods",
          "An extra commission paid to the consignee for bearing the risk of bad debts from credit sales",
          "A penalty charged to the consignor",
          "A government tax on consignment sales"
        ],
        "a": 1,
        "e": "Del credere commission is an additional commission paid to the consignee specifically for taking on the risk of bad debts arising from credit sales.",
        "h": "Recall the specific extra risk this commission compensates for.",
        "yr": "SS3"
      },
      {
        "q": "'Ordinary commission' in consignment accounting refers to:",
        "o": [
          "The extra commission for bad debt risk",
          "The normal commission paid to the consignee for selling the goods",
          "A one-time bonus unrelated to sales",
          "A commission paid only on unsold goods"
        ],
        "a": 1,
        "e": "Ordinary commission is the standard, normal commission paid to the consignee simply for successfully selling the consigned goods.",
        "h": "Think of the baseline commission, without any extra risk premium.",
        "yr": "SS3"
      },
      {
        "q": "A 'normal loss' in consignment accounting refers to:",
        "o": [
          "An unexpected loss due to fire or theft",
          "An expected, unavoidable loss (e.g., evaporation or natural wastage) absorbed into the cost of remaining stock",
          "A loss caused by the consignee's negligence only",
          "A loss that is always fully compensated by insurance"
        ],
        "a": 1,
        "e": "A normal loss is an expected, unavoidable loss such as evaporation or natural wastage, which is absorbed into the cost of the remaining stock rather than charged separately.",
        "h": "Think of losses that are a natural, expected part of handling certain goods.",
        "yr": "SS3"
      },
      {
        "q": "An 'abnormal loss' in consignment accounting refers to:",
        "o": [
          "An expected, natural wastage loss",
          "An unexpected loss (e.g., due to fire, theft, or accident) charged directly to the profit and loss account",
          "A loss that is never recorded",
          "A loss caused only by normal evaporation"
        ],
        "a": 1,
        "e": "An abnormal loss is an unexpected loss, such as from fire, theft, or accident, and is charged directly to the profit and loss account rather than spread over remaining stock.",
        "h": "Recall how abnormal losses are treated differently from normal losses.",
        "yr": "SS3"
      },
      {
        "q": "Why is the consignee NOT liable for unsold goods in a consignment arrangement?",
        "o": [
          "Because the consignee acts as an agent, not a buyer, and ownership remains with the consignor",
          "Because unsold goods are always destroyed",
          "Because the consignor has no rights over the goods once sent",
          "Because the consignee automatically owns all goods received"
        ],
        "a": 0,
        "e": "Since the consignee acts merely as an agent selling goods on behalf of the consignor, and ownership remains with the consignor, the consignee bears no liability for unsold goods.",
        "h": "Recall the agent relationship discussed in the features section.",
        "yr": "SS3"
      },
      {
        "q": "Any profit or loss on the eventual sale of consigned goods belongs to:",
        "o": [
          "The consignee",
          "The consignor",
          "Both parties equally, regardless of agreement",
          "Neither party"
        ],
        "a": 1,
        "e": "Since the consignor remains the owner of the goods throughout, any profit or loss on the eventual sale belongs to the consignor, not the consignee.",
        "h": "Recall who bears the ultimate financial outcome of the sale.",
        "yr": "SS3"
      },
      {
        "q": "Which statement correctly describes the consignee's earnings in a consignment arrangement?",
        "o": [
          "The consignee earns a profit margin like a normal trader",
          "The consignee earns a commission on sales made, not a profit margin",
          "The consignee earns nothing at all",
          "The consignee earns interest on unsold stock"
        ],
        "a": 1,
        "e": "The consignee earns a commission on the sales they make on behalf of the consignor, rather than earning a profit margin as an independent trader would.",
        "h": "Recall how the consignee is compensated for their services.",
        "yr": "SS3"
      },
      {
        "q": "Why does a proforma invoice NOT create a debtor-creditor relationship between consignor and consignee?",
        "o": [
          "Because ownership of the goods has not transferred to the consignee",
          "Because proforma invoices are illegal",
          "Because the consignee always pays cash immediately upon receiving it",
          "Because proforma invoices are the same as account sales"
        ],
        "a": 0,
        "e": "Since ownership of the goods has not transferred (the consignee is merely holding them as an agent), a proforma invoice does not create a debtor-creditor relationship, unlike a normal sales invoice.",
        "h": "Recall the exam tip explaining this distinction.",
        "yr": "SS3"
      },
      {
        "q": "Which document would show the commission earned and the net amount due to the consignor after goods have been sold?",
        "o": [
          "Proforma invoice",
          "Account sales",
          "A bank statement",
          "A tax invoice"
        ],
        "a": 1,
        "e": "The account sales document, sent by the consignee to the consignor, shows the commission earned and the net amount due after sales.",
        "h": "Recall which document reports the outcome of sales activity.",
        "yr": "SS3"
      },
      {
        "q": "In a sale transaction (unlike consignment), the risk of loss on the goods:",
        "o": [
          "Remains with the original seller indefinitely",
          "Transfers to the buyer immediately upon purchase",
          "Is shared equally between buyer and seller forever",
          "Never transfers at all"
        ],
        "a": 1,
        "e": "In an ordinary sale, the risk of loss on goods transfers to the buyer immediately upon purchase, unlike in a consignment where the consignor retains this risk until the goods are sold.",
        "h": "Compare this to the consignment vs. sale table in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Why might a consignor choose to pay a consignee del credere commission?",
        "o": [
          "To reduce the consignee's ordinary commission to zero",
          "To compensate the consignee for accepting the risk of bad debts on credit sales made on the consignor's behalf",
          "Because it is a government requirement",
          "To punish the consignee for poor performance"
        ],
        "a": 1,
        "e": "A consignor pays del credere commission specifically to compensate the consignee for taking on the additional risk of bad debts arising from credit sales made to customers.",
        "h": "Think about what additional risk this specific commission covers.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best illustrates why consignment is useful for a manufacturer wanting to sell in a distant market?",
        "o": [
          "It allows the manufacturer to retain ownership and control over pricing while using a local agent's market knowledge to sell the goods",
          "It requires the manufacturer to give up all rights to the goods immediately",
          "It eliminates the need for any agent in the distant market",
          "It guarantees the manufacturer will never incur any loss"
        ],
        "a": 0,
        "e": "Consignment allows a manufacturer to use a local agent's market knowledge and reach to sell goods in a distant market, while still retaining ownership and control until the goods are sold.",
        "h": "Think about the practical business advantage of this arrangement.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would be classified as a normal loss rather than an abnormal loss?",
        "o": [
          "Goods destroyed by an unexpected warehouse fire",
          "Goods stolen during transportation",
          "A small percentage of liquid goods lost to evaporation during storage",
          "Goods damaged due to the consignee's careless handling"
        ],
        "a": 2,
        "e": "Evaporation during storage is a normal, expected, and largely unavoidable loss, unlike fire, theft, or careless handling, which are treated as abnormal losses.",
        "h": "Look for the loss type that is a natural, expected occurrence rather than an accident or negligence.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Consignment Account – Accounting Treatment",
    "topicCode": "SS3-ACC-02",
    "module": "Consignment Accounts",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  This topic covers the actual <span class=\"learn-keyword\">ledger entries</span> needed to record a consignment transaction in the books of the consignor, including how to value unsold stock and treat abnormal losses.\n</div>\n\n<h3 class=\"learn-subheading\">1. Three Key Accounts Prepared by the Consignor</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Account</th><th>Purpose</th></tr></thead>\n    <tbody>\n      <tr><td>Consignment Account</td><td>Records the cost of goods sent, expenses, sales, commission, and shows the profit or loss on consignment</td></tr>\n      <tr><td>Consignee's (Agent's) Account</td><td>A personal account tracking amounts owed by the consignee to the consignor</td></tr>\n      <tr><td>Goods Sent on Consignment Account</td><td>Records the value of goods sent out, eventually transferred to the trading account</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Recording Expenses</h3>\n<p class=\"learn-p\">Expenses on consignment are split into two types:</p>\n<ul class=\"learn-list\">\n  <li><strong>Consignor's expenses:</strong> e.g., packing, freight, insurance paid before goods leave the consignor — added to the cost of goods for stock valuation</li>\n  <li><strong>Consignee's expenses:</strong> e.g., carriage from port, godown rent, selling expenses paid by the consignee — also added to the total cost for stock valuation, then reimbursed to the consignee</li>\n</ul>\n\n<h3 class=\"learn-subheading\">3. Valuation of Unsold Stock</h3>\n<p class=\"learn-p\">Unsold stock at the end of the period is valued at <strong>cost price plus a proportionate share of all expenses</strong> (both consignor's and consignee's, but only non-selling expenses) incurred up to the point of it being ready for sale. This ensures the stock is valued consistently with the goods that were sold.</p>\n<p class=\"learn-p\">Formula: <strong>Value of unsold stock = (Cost of goods sent + Proportionate expenses) × (Unsold units ÷ Total units sent)</strong></p>\n\n<h3 class=\"learn-subheading\">4. Treatment of Abnormal Loss</h3>\n<p class=\"learn-p\">When an abnormal loss occurs (e.g., goods destroyed by fire), its value — calculated using the same proportionate method as unsold stock — is removed from the consignment account and charged directly to the profit and loss account, since it should not affect the normal trading profit on consignment.</p>\n\n<h3 class=\"learn-subheading\">5. Simple Worked Example</h3>\n<p class=\"learn-p\">If goods costing ₦100,000 are sent on consignment, and the consignor pays ₦10,000 freight, total cost = ₦110,000 for say 1,000 units. If 800 units are sold and 200 remain unsold, the value of unsold stock = ₦110,000 × (200/1000) = ₦22,000.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Only NON-selling expenses (like freight and insurance) are included when valuing unsold stock. Selling expenses (like advertising or commission) are NOT added, since unsold goods have not yet incurred those costs.\n</div>\n",
    "questions": [
      {
        "q": "The 'Consignment Account' prepared by the consignor is used to:",
        "o": [
          "Record only the consignee's personal bank details",
          "Record the cost of goods sent, expenses, sales, commission, and show the profit or loss on consignment",
          "Record only government taxes",
          "Track the consignor's unrelated day-to-day sales"
        ],
        "a": 1,
        "e": "The Consignment Account records the cost of goods sent, associated expenses, sales made, commission, and ultimately reveals the profit or loss on the consignment.",
        "h": "Recall the first account described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "The 'Consignee's Account' is best described as:",
        "o": [
          "A trading account for the consignor's other business",
          "A personal account tracking amounts owed by the consignee to the consignor",
          "An account with no financial purpose",
          "A record of the consignor's expenses only"
        ],
        "a": 1,
        "e": "The Consignee's Account is a personal account that tracks the amounts owed by the consignee to the consignor, based on sales made and expenses incurred.",
        "h": "Think of this as similar to a debtor's account.",
        "yr": "SS3"
      },
      {
        "q": "The 'Goods Sent on Consignment Account' is eventually transferred to the:",
        "o": [
          "Consignee's personal account",
          "Trading account",
          "Profit and loss account directly, bypassing trading account",
          "Bank account"
        ],
        "a": 1,
        "e": "The Goods Sent on Consignment Account records the value of goods dispatched and is eventually transferred to the trading account.",
        "h": "Recall where this account's balance ultimately gets transferred.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an example of a consignor's expense on consignment?",
        "o": [
          "Godown rent paid by the consignee",
          "Packing and freight paid before goods leave the consignor",
          "Selling expenses paid by the consignee",
          "Commission paid to the consignee"
        ],
        "a": 1,
        "e": "Packing and freight paid by the consignor before goods are dispatched are classified as consignor's expenses.",
        "h": "Look for the expense incurred before the goods even leave the consignor.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an example of a consignee's expense on consignment?",
        "o": [
          "Packing done at the consignor's warehouse",
          "Freight paid by the consignor before dispatch",
          "Godown rent and carriage from port, paid by the consignee",
          "Insurance paid by the consignor before dispatch"
        ],
        "a": 2,
        "e": "Godown rent and carriage from the port are examples of consignee's expenses, since they are incurred by the consignee after receiving the goods.",
        "h": "Look for expenses incurred after the goods arrive with the consignee.",
        "yr": "SS3"
      },
      {
        "q": "Unsold stock at the end of a consignment period is valued at:",
        "o": [
          "Selling price only, with no cost consideration",
          "Cost price plus a proportionate share of relevant expenses incurred up to the point of it being ready for sale",
          "Zero value, since it has not been sold",
          "The full total cost of all goods sent, regardless of quantity unsold"
        ],
        "a": 1,
        "e": "Unsold stock is valued at cost price plus a proportionate share of relevant (non-selling) expenses incurred up to the point of being ready for sale.",
        "h": "Recall the valuation principle explained in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which type of expense is EXCLUDED when calculating the value of unsold stock?",
        "o": [
          "Freight paid before dispatch",
          "Insurance paid before dispatch",
          "Selling expenses, such as advertising or commission",
          "Packing costs"
        ],
        "a": 2,
        "e": "Selling expenses, such as advertising or commission, are excluded from the valuation of unsold stock since unsold goods have not yet incurred those specific costs.",
        "h": "Recall the exam tip about which expenses are excluded.",
        "yr": "SS3"
      },
      {
        "q": "Using the worked example in the lesson, if total cost (goods + freight) is ₦110,000 for 1,000 units, and 200 units remain unsold, what is the value of the unsold stock?",
        "o": [
          "₦110,000",
          "₦22,000",
          "₦88,000",
          "₦11,000"
        ],
        "a": 1,
        "e": "Value of unsold stock = ₦110,000 × (200/1000) = ₦22,000, following the proportionate valuation method.",
        "h": "Apply the formula: Total cost × (unsold units ÷ total units).",
        "yr": "SS3"
      },
      {
        "q": "In the same worked example, what was the value of goods actually sold (800 units) in terms of cost?",
        "o": [
          "₦22,000",
          "₦88,000",
          "₦110,000",
          "₦100,000"
        ],
        "a": 1,
        "e": "Since total cost is ₦110,000 for 1,000 units, the cost of 800 sold units = ₦110,000 × (800/1000) = ₦88,000.",
        "h": "Apply the same proportionate method to the sold units instead.",
        "yr": "SS3"
      },
      {
        "q": "When an abnormal loss occurs (e.g., goods destroyed by fire), how should its value be treated?",
        "o": [
          "Included in the value of unsold stock",
          "Removed from the consignment account and charged directly to the profit and loss account",
          "Ignored completely with no accounting entry",
          "Added to the consignee's commission"
        ],
        "a": 1,
        "e": "The value of an abnormal loss is removed from the consignment account and charged directly to the profit and loss account, since it should not distort the normal trading profit on consignment.",
        "h": "Recall the specific treatment described for abnormal losses.",
        "yr": "SS3"
      },
      {
        "q": "Why is an abnormal loss charged directly to the profit and loss account rather than absorbed into remaining stock value?",
        "o": [
          "Because it should not affect or distort the normal trading profit calculated on the consignment",
          "Because abnormal losses never have a calculable value",
          "Because it is illegal to record abnormal losses",
          "Because abnormal losses always equal zero"
        ],
        "a": 0,
        "e": "Charging abnormal losses directly to the profit and loss account ensures they do not distort the calculation of normal trading profit on the consignment.",
        "h": "Recall the reasoning given in the lesson for this treatment.",
        "yr": "SS3"
      },
      {
        "q": "The value of an abnormal loss is calculated using:",
        "o": [
          "A completely different, unrelated method from stock valuation",
          "The same proportionate method used for valuing unsold stock",
          "Only the selling price of the goods",
          "A random estimate with no formula"
        ],
        "a": 1,
        "e": "The value of an abnormal loss is calculated using the same proportionate cost-plus-expenses method used for valuing unsold stock.",
        "h": "Recall that the same valuation approach applies to both unsold stock and abnormal losses.",
        "yr": "SS3"
      },
      {
        "q": "Which formula correctly represents the valuation of unsold stock?",
        "o": [
          "(Cost of goods sent + Proportionate expenses) × (Unsold units ÷ Total units sent)",
          "Cost of goods sent ÷ Unsold units",
          "Selling price × Total units sent",
          "Proportionate expenses only, ignoring cost of goods"
        ],
        "a": 0,
        "e": "The correct formula is (Cost of goods sent + Proportionate expenses) × (Unsold units ÷ Total units sent), as given in the lesson.",
        "h": "Recall the exact formula provided in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Consignee's non-selling expenses, such as godown rent, are:",
        "o": [
          "Never included in stock valuation",
          "Added to the total cost for stock valuation purposes, then reimbursed to the consignee",
          "Deducted from the consignor's profit with no further treatment",
          "Ignored entirely in accounting"
        ],
        "a": 1,
        "e": "Consignee's non-selling expenses, like godown rent, are added to the total cost for stock valuation purposes and are also reimbursed to the consignee.",
        "h": "Recall how consignee's expenses are treated in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Why does the consignor need to prepare a separate 'Goods Sent on Consignment Account'?",
        "o": [
          "To record the value of goods dispatched separately, before transferring the balance to the trading account",
          "Because this account has no accounting purpose",
          "To directly calculate the consignee's commission",
          "To replace the need for a Consignment Account"
        ],
        "a": 0,
        "e": "The Goods Sent on Consignment Account separately records the value of goods dispatched on consignment before its balance is transferred to the trading account.",
        "h": "Recall the specific purpose of this account described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "If 1,000 units costing a total of ₦110,000 (including freight) are sent, and all units are eventually sold with no loss, what would the closing stock valuation be?",
        "o": [
          "₦110,000",
          "₦0, since no units remain unsold",
          "₦55,000",
          "₦22,000"
        ],
        "a": 1,
        "e": "If all units are sold with none remaining, the closing stock valuation would be ₦0, since there is no unsold stock to value.",
        "h": "Think about what 'unsold units' would be if everything was sold.",
        "yr": "SS3"
      },
      {
        "q": "Which of these accounts would show the consignor the amount still owed by the consignee at a given point in time?",
        "o": [
          "Goods Sent on Consignment Account",
          "Consignee's (Agent's) Account",
          "Trading Account",
          "Profit and Loss Account only"
        ],
        "a": 1,
        "e": "The Consignee's (Agent's) Account, being a personal account, shows the consignor the amount still owed by the consignee at any point in time.",
        "h": "Recall which account tracks amounts owed by the consignee.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following expenses would be correctly EXCLUDED from the valuation of unsold stock?",
        "o": [
          "Freight paid to transport goods to the consignee",
          "Insurance on the goods during transit",
          "Advertising expenses paid to promote the sale of goods",
          "Packing costs before dispatch"
        ],
        "a": 2,
        "e": "Advertising expenses are selling expenses and are excluded from stock valuation, unlike freight, insurance, and packing, which relate to getting the goods ready for sale.",
        "h": "Look for the expense specifically related to promoting sales, not preparing goods for sale.",
        "yr": "SS3"
      },
      {
        "q": "Correct accounting treatment of a consignment ensures that:",
        "o": [
          "The consignor's true profit or loss on the consignment is accurately reflected, separate from normal trading activities",
          "The consignee automatically becomes the owner of all goods",
          "No distinction is made between normal and abnormal losses",
          "Unsold stock is always valued at zero"
        ],
        "a": 0,
        "e": "Proper consignment accounting ensures the consignor's true profit or loss specifically from the consignment activity is accurately calculated and kept separate from other trading activities.",
        "h": "Think about the overall purpose of preparing these specific accounts.",
        "yr": "SS3"
      },
      {
        "q": "If an abnormal loss of 50 units occurs (from the 1,000 units sent, valued at ₦110,000 total cost), what would be the value of this abnormal loss, using the proportionate method?",
        "o": [
          "₦110,000",
          "₦5,500",
          "₦55,000",
          "₦0"
        ],
        "a": 1,
        "e": "Value of abnormal loss = ₦110,000 × (50/1000) = ₦5,500, using the same proportionate valuation method as for unsold stock.",
        "h": "Apply the same formula used for unsold stock valuation to the lost units instead.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Joint Venture Account",
    "topicCode": "SS3-ACC-03",
    "module": "Joint Venture Accounts",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">joint venture</span> is a temporary business arrangement between two or more people (co-venturers) who combine resources to carry out a specific business transaction or project, sharing the profit or loss according to an agreed ratio.\n</div>\n\n<h3 class=\"learn-subheading\">1. Features of a Joint Venture</h3>\n<ul class=\"learn-list\">\n  <li>It is formed for a specific, one-off transaction or project (e.g., a construction project, an import deal)</li>\n  <li>It automatically ends once the venture's purpose is completed</li>\n  <li>There is no formal firm name required, and each co-venturer can carry on other separate businesses simultaneously</li>\n  <li>Profit or loss is shared according to an agreed ratio, similar to a partnership</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Joint Venture vs. Partnership</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Joint Venture</th><th>Partnership</th></tr></thead>\n    <tbody>\n      <tr><td>Temporary — ends after the specific project is complete</td><td>Ongoing — continues until dissolved by agreement</td></tr>\n      <tr><td>No firm name is generally required</td><td>Usually operates under a firm name</td></tr>\n      <tr><td>Co-venturers can run other independent businesses at the same time</td><td>Partners are usually restricted from competing businesses</td></tr>\n      <tr><td>No separate set of books is compulsory (though it may be kept)</td><td>A full, continuous set of books is always maintained</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Accounting Method 1: Separate Set of Books</h3>\n<p class=\"learn-p\">Under this method, a completely separate Joint Venture Account (and Joint Bank Account, if needed) is opened, and both co-venturers record their contributions and transactions in this shared set of books. At the end, profit or loss is calculated and distributed per the agreed ratio.</p>\n\n<h3 class=\"learn-subheading\">4. Accounting Method 2: Memorandum Joint Venture Account Method</h3>\n<p class=\"learn-p\">Here, no separate set of books is kept. Instead, each co-venturer records the venture's transactions in their OWN personal books using a \"Joint Venture with [other party]\" account. A separate \"Memorandum Joint Venture Account\" is prepared (not part of the double-entry system) purely to calculate the overall profit or loss, which is then shared and recorded in each co-venturer's own books.</p>\n\n<h3 class=\"learn-subheading\">5. When to Use Each Method</h3>\n<p class=\"learn-p\">The separate set of books method suits ventures where a joint bank account and shared records are practical. The memorandum method is more common when co-venturers are in different locations and prefer to keep the venture's records within their own individual books.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> A Memorandum Joint Venture Account is NOT part of the double-entry system — it's a memorandum (side calculation) used only to determine total profit or loss, which is then entered into each co-venturer's personal Joint Venture account.\n</div>\n",
    "questions": [
      {
        "q": "A 'joint venture' is best described as:",
        "o": [
          "A permanent partnership lasting indefinitely",
          "A temporary arrangement between two or more people to carry out a specific business transaction, sharing profit or loss",
          "A loan agreement between two banks",
          "A single individual's personal business"
        ],
        "a": 1,
        "e": "A joint venture is a temporary business arrangement between co-venturers formed to carry out a specific transaction or project, with profit or loss shared per an agreed ratio.",
        "h": "Focus on the word 'temporary' and 'specific transaction'.",
        "yr": "SS3"
      },
      {
        "q": "A joint venture automatically ends when:",
        "o": [
          "One partner dies, regardless of the venture's status",
          "The venture's specific purpose or project is completed",
          "A new co-venturer joins",
          "It never ends automatically"
        ],
        "a": 1,
        "e": "A joint venture is formed for a specific purpose and automatically ends once that purpose or project has been completed.",
        "h": "Recall the temporary nature described in the lesson's features.",
        "yr": "SS3"
      },
      {
        "q": "In a joint venture, co-venturers can:",
        "o": [
          "Never run any other business simultaneously",
          "Carry on other separate businesses simultaneously alongside the joint venture",
          "Only work for the joint venture, exclusively, for life",
          "Never share profits with each other"
        ],
        "a": 1,
        "e": "Unlike some partnership restrictions, co-venturers in a joint venture can typically carry on other separate businesses simultaneously.",
        "h": "Recall the flexibility feature listed for joint ventures.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best distinguishes a joint venture from a partnership?",
        "o": [
          "A joint venture is temporary and project-specific; a partnership is ongoing until formally dissolved",
          "They are exactly identical in every respect",
          "A partnership always ends after one transaction",
          "A joint venture always requires a formal firm name"
        ],
        "a": 0,
        "e": "A joint venture is temporary, ending once its specific project is complete, whereas a partnership is an ongoing arrangement that continues until formally dissolved.",
        "h": "Recall the comparison table given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Under the 'Separate Set of Books' method for joint ventures, transactions are recorded in:",
        "o": [
          "Each co-venturer's own individual personal books only",
          "A completely separate, shared set of books opened for the joint venture",
          "No books at all",
          "Only a memorandum account, with no ledger entries"
        ],
        "a": 1,
        "e": "The Separate Set of Books method involves opening a completely separate, shared set of books (including a Joint Venture Account) specifically for recording the venture's transactions.",
        "h": "Recall the first accounting method described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Under the 'Memorandum Joint Venture Account' method, transactions are recorded in:",
        "o": [
          "A completely separate shared set of books",
          "Each co-venturer's own personal books, using a 'Joint Venture with [other party]' account",
          "No records are kept at all",
          "Only a government-mandated register"
        ],
        "a": 1,
        "e": "Under the Memorandum method, each co-venturer records the venture's transactions in their own personal books, using an account like 'Joint Venture with [other party]'.",
        "h": "Recall the second accounting method described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "A 'Memorandum Joint Venture Account' is best described as:",
        "o": [
          "A fully part of the double-entry bookkeeping system",
          "A side calculation, NOT part of the double-entry system, used purely to determine overall profit or loss",
          "A legal contract document",
          "A tax filing document"
        ],
        "a": 1,
        "e": "The Memorandum Joint Venture Account is not part of the formal double-entry system; it is a memorandum used purely to calculate the total profit or loss of the venture.",
        "h": "Recall the exam tip explaining this account's nature.",
        "yr": "SS3"
      },
      {
        "q": "After the overall profit or loss is calculated in a Memorandum Joint Venture Account, it is then:",
        "o": [
          "Ignored, with no further action",
          "Shared according to the agreed ratio and recorded in each co-venturer's own personal Joint Venture account",
          "Given entirely to one co-venturer only",
          "Kept only within the memorandum account permanently"
        ],
        "a": 1,
        "e": "After calculating the overall profit or loss, it is shared according to the agreed ratio and recorded appropriately in each co-venturer's own personal Joint Venture account.",
        "h": "Recall the final step described for the memorandum method.",
        "yr": "SS3"
      },
      {
        "q": "Which accounting method is generally more suitable when co-venturers are located in different places and prefer to maintain their own individual records?",
        "o": [
          "Separate Set of Books method",
          "Memorandum Joint Venture Account method",
          "Neither method applies in such a case",
          "A joint venture cannot exist across different locations"
        ],
        "a": 1,
        "e": "The Memorandum Joint Venture Account method is generally more suitable when co-venturers are in different locations and prefer to keep records within their own individual books.",
        "h": "Recall the guidance given on when to use each method.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is generally NOT a requirement for forming a joint venture, unlike a partnership?",
        "o": [
          "A formal firm name",
          "An agreed profit-sharing ratio",
          "Contribution of resources by co-venturers",
          "A specific business purpose"
        ],
        "a": 0,
        "e": "Unlike a partnership, a joint venture generally does not require a formal firm name, since it is typically formed for a specific, temporary purpose.",
        "h": "Recall the comparison table's point about firm names.",
        "yr": "SS3"
      },
      {
        "q": "Profit or loss in a joint venture is shared:",
        "o": [
          "Equally between all parties, regardless of any agreement",
          "According to an agreed ratio between the co-venturers",
          "Only by the co-venturer who contributed the most capital, with none for others",
          "Randomly with no formal agreement"
        ],
        "a": 1,
        "e": "Profit or loss in a joint venture is shared according to a ratio previously agreed upon by the co-venturers, similar to how partnerships share profits.",
        "h": "Recall how profit-sharing is described in the lesson's features.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would most likely be organised as a joint venture rather than a formal partnership?",
        "o": [
          "Two friends opening a permanent retail shop together for the long term",
          "Two business owners combining resources for a single, specific construction project that will end once completed",
          "A company continuing to operate indefinitely under one firm name",
          "A sole trader running a single, ongoing personal business"
        ],
        "a": 1,
        "e": "A single, specific construction project with a defined end point is a classic example suited to a joint venture, given its temporary, project-specific nature.",
        "h": "Look for the option describing a one-off, project-based collaboration.",
        "yr": "SS3"
      },
      {
        "q": "Compared to a partnership's continuous set of books, a joint venture:",
        "o": [
          "Always requires an identical continuous set of books",
          "Does not compulsorily require a continuous set of books, though one may be kept under the separate books method",
          "Is legally required to have no records whatsoever",
          "Must use only government-provided books"
        ],
        "a": 1,
        "e": "Unlike a partnership's continuous books, a joint venture does not compulsorily require a continuous set of books — though a separate set may be used under one of the two accounting methods.",
        "h": "Recall the comparison table's point about record-keeping requirements.",
        "yr": "SS3"
      },
      {
        "q": "In the Separate Set of Books method, if a Joint Bank Account is also opened, its purpose is to:",
        "o": [
          "Record only one co-venturer's personal unrelated transactions",
          "Handle the joint venture's shared cash transactions, contributions, and payments",
          "Replace the need for a Joint Venture Account entirely",
          "Serve no practical accounting purpose"
        ],
        "a": 1,
        "e": "A Joint Bank Account, when opened under the Separate Set of Books method, is used to handle the joint venture's shared cash transactions, including contributions and payments.",
        "h": "Think about the practical role of a shared bank account in this context.",
        "yr": "SS3"
      },
      {
        "q": "Which statement is TRUE regarding a co-venturer's ability to engage in other business activities?",
        "o": [
          "Co-venturers are typically free to engage in other independent businesses alongside the joint venture",
          "Co-venturers are legally barred from any other business activity",
          "Only one co-venturer may have other businesses, never both",
          "This is not addressed anywhere in joint venture principles"
        ],
        "a": 0,
        "e": "Co-venturers in a joint venture are typically free to engage in other independent businesses alongside their involvement in the joint venture.",
        "h": "Recall the flexibility feature contrasted with partnership restrictions.",
        "yr": "SS3"
      },
      {
        "q": "Why might two importers form a joint venture rather than a full partnership for a single shipment deal?",
        "o": [
          "Because a joint venture suits a single, temporary transaction without the ongoing commitment of a formal partnership",
          "Because joint ventures are illegal for import businesses",
          "Because partnerships cannot involve more than one transaction ever",
          "Because joint ventures always require government approval, unlike partnerships"
        ],
        "a": 0,
        "e": "A joint venture is well suited to a single, temporary transaction like one shipment deal, without requiring the long-term, ongoing commitment of a formal partnership.",
        "h": "Think about matching the business structure to the temporary nature of the deal.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following best completes: 'Since the Memorandum Joint Venture Account is not part of the double-entry system, it is used only to ______.'",
        "o": [
          "Record daily unrelated transactions",
          "Calculate the overall profit or loss to be shared between co-venturers",
          "Replace personal books entirely",
          "Pay taxes directly to the government"
        ],
        "a": 1,
        "e": "The Memorandum Joint Venture Account exists purely as a side calculation to determine the overall profit or loss to be shared among co-venturers.",
        "h": "Recall the specific limited purpose of this memorandum account.",
        "yr": "SS3"
      },
      {
        "q": "If two co-venturers agree to share profits in a 60:40 ratio and the joint venture makes a profit of ₦100,000, how much would the co-venturer with the 60% share receive?",
        "o": [
          "₦40,000",
          "₦50,000",
          "₦60,000",
          "₦100,000"
        ],
        "a": 2,
        "e": "60% of ₦100,000 = ₦60,000, which would be the share received by the co-venturer with the 60% agreed ratio.",
        "h": "Calculate 60% of the total profit.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a similarity between joint ventures and partnerships?",
        "o": [
          "Both involve two or more parties contributing resources and sharing profit or loss according to an agreed ratio",
          "Both must always use a formal firm name",
          "Both are always permanent, ongoing arrangements",
          "Neither involves any form of profit-sharing"
        ],
        "a": 0,
        "e": "Both joint ventures and partnerships involve two or more parties contributing resources and sharing profit or loss based on an agreed ratio, despite their differences in duration and formality.",
        "h": "Look for the shared feature despite their key differences.",
        "yr": "SS3"
      },
      {
        "q": "Which accounting method would be most practical if two co-venturers, in the same city, wanted a single shared record of all joint transactions, including a joint bank account?",
        "o": [
          "Memorandum Joint Venture Account method",
          "Separate Set of Books method",
          "Neither method is ever practical",
          "A method involving no records at all"
        ],
        "a": 1,
        "e": "The Separate Set of Books method, which involves a shared, separate set of books (and potentially a joint bank account), is most practical when co-venturers want a single shared record.",
        "h": "Recall which method involves a completely separate, shared set of records.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Contract Account",
    "topicCode": "SS3-ACC-04",
    "module": "Contract Accounts",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">contract account</span> is used to record the costs and revenue of a large-scale, long-duration job — such as building construction or road building — that typically spans more than one accounting period.\n</div>\n\n<h3 class=\"learn-subheading\">1. Contract Costing vs. Job Costing</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Contract Costing</th><th>Job Costing</th></tr></thead>\n    <tbody>\n      <tr><td>Used for large-scale, long-duration projects (e.g., building a bridge)</td><td>Used for smaller, shorter-duration jobs (e.g., repairing a machine)</td></tr>\n      <tr><td>Work is often carried out at the client's site</td><td>Work is usually carried out at the contractor's own premises/workshop</td></tr>\n      <tr><td>Costs are directly identifiable with the single large contract</td><td>Costs may be shared across multiple smaller jobs</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Key Terms in Contract Accounts</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Meaning</th></tr></thead>\n    <tbody>\n      <tr><td>Contract price</td><td>The total agreed amount the client will pay for the completed contract</td></tr>\n      <tr><td>Work-in-progress</td><td>The value of work completed on the contract but not yet certified or fully paid for, at a given point in time</td></tr>\n      <tr><td>Work certified</td><td>The value of work inspected and approved by the client's architect/surveyor as satisfactorily completed</td></tr>\n      <tr><td>Progress payment</td><td>Instalment payments made by the client to the contractor as work is certified, rather than waiting until full completion</td></tr>\n      <tr><td>Retention money</td><td>A percentage of the certified work value withheld by the client as security, released only after the contract is fully complete and any defects are fixed</td></tr>\n      <tr><td>Notional profit</td><td>The estimated profit on an incomplete contract, calculated at a point during the contract, before final completion</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Why Retention Money Matters</h3>\n<p class=\"learn-p\">Retention money protects the client by ensuring the contractor has a financial incentive to complete the contract properly and fix any defects, since a portion of payment is only released after satisfactory final completion.</p>\n\n<h3 class=\"learn-subheading\">4. Calculating Profit on Incomplete Contracts</h3>\n<p class=\"learn-p\">Since a contract may span several accounting years, an estimate of profit — the <span class=\"learn-keyword\">notional profit</span> — must be calculated for work done so far. A common prudent rule is to transfer only a portion of the notional profit to the profit and loss account (rather than the full amount), retaining the rest as a reserve against possible future losses or unforeseen costs on the remaining work.</p>\n<p class=\"learn-p\">A widely used formula for cash received based on work certified: <strong>Notional profit transferred to P&L = Notional Profit × (Cash received ÷ Work certified) × (2/3)</strong>, though the exact fraction used may vary by convention or exam instruction.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Do not confuse \"work certified\" (approved by the architect/surveyor) with \"work-in-progress\" (all work done so far, whether certified or not) — WAEC often tests this distinction in contract account questions.\n</div>\n",
    "questions": [
      {
        "q": "A 'contract account' is primarily used to record the costs and revenue of:",
        "o": [
          "A small, short-duration repair job",
          "A large-scale, long-duration job typically spanning more than one accounting period",
          "A single day's retail sales",
          "A personal savings account"
        ],
        "a": 1,
        "e": "A contract account is used for large-scale, long-duration projects, such as building construction, which often span more than one accounting period.",
        "h": "Focus on the scale and duration described in the definition.",
        "yr": "SS3"
      },
      {
        "q": "Which best distinguishes contract costing from job costing?",
        "o": [
          "Contract costing is for large, long-duration projects; job costing is for smaller, shorter-duration jobs",
          "They are exactly the same costing method",
          "Job costing is always larger in scale than contract costing",
          "Contract costing never involves client sites"
        ],
        "a": 0,
        "e": "Contract costing is used for large-scale, long-duration projects, while job costing applies to smaller, shorter-duration jobs, often carried out at the contractor's own premises.",
        "h": "Recall the comparison table given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Work under contract costing is typically carried out at:",
        "o": [
          "The contractor's own workshop only",
          "The client's site",
          "A completely unrelated third-party location",
          "No physical location is ever specified"
        ],
        "a": 1,
        "e": "Contract costing work is typically carried out at the client's site, unlike job costing, which is usually done at the contractor's own premises.",
        "h": "Recall the comparison table's point about work location.",
        "yr": "SS3"
      },
      {
        "q": "The 'contract price' refers to:",
        "o": [
          "The value of work completed but not yet paid for",
          "The total agreed amount the client will pay for the completed contract",
          "The amount withheld as security by the client",
          "The contractor's estimated profit only"
        ],
        "a": 1,
        "e": "The contract price is the total agreed amount that the client has agreed to pay for the entire completed contract.",
        "h": "Recall the first key term listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "'Work-in-progress' in a contract account refers to:",
        "o": [
          "Only the work that has been certified by the architect",
          "The value of work completed on the contract but not yet certified or fully paid for, at a given point in time",
          "The final completed contract value",
          "The retention money withheld"
        ],
        "a": 1,
        "e": "Work-in-progress refers to the value of work completed on the contract at a given point, regardless of whether it has been formally certified yet.",
        "h": "Recall the exam tip distinguishing this from 'work certified'.",
        "yr": "SS3"
      },
      {
        "q": "'Work certified' refers to:",
        "o": [
          "All work done, whether inspected or not",
          "The value of work inspected and approved by the client's architect/surveyor as satisfactorily completed",
          "The contractor's personal opinion of progress",
          "The total contract price agreed at the start"
        ],
        "a": 1,
        "e": "Work certified specifically refers to the value of work that has been inspected and formally approved by the client's architect or surveyor.",
        "h": "Focus on the formal approval aspect of this term.",
        "yr": "SS3"
      },
      {
        "q": "A 'progress payment' is best described as:",
        "o": [
          "A single lump-sum payment made only at the very end of the contract",
          "Instalment payments made by the client to the contractor as work is certified",
          "A penalty charged to the contractor for delays",
          "Money withheld permanently by the client"
        ],
        "a": 1,
        "e": "Progress payments are instalment payments made by the client to the contractor as portions of work are certified, rather than a single payment at full completion.",
        "h": "Think of payments made 'as you go' rather than all at once.",
        "yr": "SS3"
      },
      {
        "q": "'Retention money' refers to:",
        "o": [
          "The full contract price paid upfront",
          "A percentage of certified work value withheld by the client as security until final completion",
          "Money paid to the architect only",
          "A tax deducted by the government"
        ],
        "a": 1,
        "e": "Retention money is a percentage of the certified work value that the client withholds as security, releasing it only after the contract is fully and satisfactorily complete.",
        "h": "Recall the security purpose behind this withheld amount.",
        "yr": "SS3"
      },
      {
        "q": "The main purpose of retention money is to:",
        "o": [
          "Punish the contractor unfairly",
          "Give the client financial protection and incentivise the contractor to properly complete the contract and fix defects",
          "Reduce the total contract price permanently",
          "Eliminate the need for progress payments"
        ],
        "a": 1,
        "e": "Retention money protects the client by giving the contractor a financial incentive to complete the contract properly and address any defects before full payment is released.",
        "h": "Recall the explanation given for why retention money matters.",
        "yr": "SS3"
      },
      {
        "q": "'Notional profit' refers to:",
        "o": [
          "The final, audited profit after the contract is fully complete",
          "The estimated profit on an incomplete contract, calculated at a point during the contract",
          "A loss that must always be recorded",
          "The contractor's total revenue for the year"
        ],
        "a": 1,
        "e": "Notional profit is an estimated profit figure calculated on an incomplete contract, at a specific point before the contract's final completion.",
        "h": "Recall the definition given for this specific type of profit.",
        "yr": "SS3"
      },
      {
        "q": "Why is only a portion of notional profit typically transferred to the profit and loss account, rather than the full amount?",
        "o": [
          "Because none of it should ever be recognised",
          "As a prudent measure, retaining the rest as a reserve against possible future losses or unforeseen costs on the remaining work",
          "Because it is illegal to record any notional profit",
          "Because the full amount is always transferred, with no exceptions"
        ],
        "a": 1,
        "e": "A prudent accounting approach retains part of the notional profit as a reserve against possible future losses or unforeseen costs before the contract is fully complete, transferring only a portion to the profit and loss account.",
        "h": "Recall the prudence principle discussed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best distinguishes 'work certified' from 'work-in-progress'?",
        "o": [
          "Work certified is specifically approved by the architect/surveyor; work-in-progress includes all work done, whether certified or not",
          "They are exactly the same concept",
          "Work-in-progress is always higher in value than the total contract price",
          "Work certified always equals the full contract price"
        ],
        "a": 0,
        "e": "Work certified is the portion of work formally approved by the architect or surveyor, while work-in-progress includes all work completed so far, whether or not it has been certified.",
        "h": "Recall the exam tip specifically addressing this distinction.",
        "yr": "SS3"
      },
      {
        "q": "If a contractor completes work certified at ₦500,000 and the client's retention rate is 10%, how much retention money would be withheld?",
        "o": [
          "₦5,000",
          "₦50,000",
          "₦500,000",
          "₦450,000"
        ],
        "a": 1,
        "e": "10% of ₦500,000 = ₦50,000, which would be the retention money withheld by the client.",
        "h": "Calculate 10% of the work certified value.",
        "yr": "SS3"
      },
      {
        "q": "Using the same example, how much would the contractor actually receive as a progress payment (after retention)?",
        "o": [
          "₦500,000",
          "₦450,000",
          "₦50,000",
          "₦550,000"
        ],
        "a": 1,
        "e": "₦500,000 (work certified) minus ₦50,000 (retention money) = ₦450,000, the amount the contractor would actually receive as the progress payment.",
        "h": "Subtract the retention money from the work certified value.",
        "yr": "SS3"
      },
      {
        "q": "A contract for building a large dam, expected to take three years to complete, would most appropriately use:",
        "o": [
          "Job costing",
          "Contract costing",
          "No costing method at all",
          "A single-day sales record"
        ],
        "a": 1,
        "e": "A large-scale, multi-year project like building a dam is best suited to contract costing, given its scale and long duration.",
        "h": "Recall the type of project associated with contract costing.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would most likely use job costing rather than contract costing?",
        "o": [
          "Constructing a multi-storey office building over two years",
          "Repairing a single customer's car engine at the workshop",
          "Building a new highway spanning several kilometres",
          "Constructing a large stadium"
        ],
        "a": 1,
        "e": "Repairing a single car engine at the workshop is a smaller, shorter-duration task, making it suitable for job costing rather than contract costing.",
        "h": "Look for the smaller-scale, shorter task among the options.",
        "yr": "SS3"
      },
      {
        "q": "Why might a contractor be concerned about the size of the retention percentage set by a client?",
        "o": [
          "Because a higher retention percentage means more of the contractor's earned payment is withheld until final completion",
          "Because retention money has no effect on the contractor's cash flow",
          "Because retention money is always paid immediately regardless of percentage",
          "Because retention percentages never vary between contracts"
        ],
        "a": 0,
        "e": "A higher retention percentage means a larger portion of the contractor's earned payment is withheld until the contract is fully and satisfactorily completed, which can affect the contractor's cash flow.",
        "h": "Think about the cash flow implications of a higher retention rate.",
        "yr": "SS3"
      },
      {
        "q": "The concept of notional profit exists specifically because:",
        "o": [
          "Contracts always finish within a single accounting year, avoiding any need for estimation",
          "Long-duration contracts may span multiple accounting periods, requiring an estimated profit figure for work done so far",
          "Contractors never make any profit until the contract is fully complete",
          "Notional profit is unrelated to accounting periods"
        ],
        "a": 1,
        "e": "Since long-duration contracts often span multiple accounting periods, notional profit provides a way to estimate profit for work completed so far, before the contract's final completion.",
        "h": "Recall why an interim profit estimate is needed for long contracts.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a key term specifically related to payments made incrementally during a long contract, rather than as one final lump sum?",
        "o": [
          "Contract price",
          "Progress payment",
          "Notional profit",
          "Work-in-progress"
        ],
        "a": 1,
        "e": "Progress payment specifically refers to incremental instalment payments made during the course of a long contract, based on certified work.",
        "h": "Recall the term describing payments made 'as you go'.",
        "yr": "SS3"
      },
      {
        "q": "Which of these best explains why contract costing typically identifies costs directly with a single large project, unlike job costing?",
        "o": [
          "Because contract costing deals with one large, distinct project, so its costs are not shared across multiple smaller jobs, unlike job costing",
          "Because contract costing never tracks any costs at all",
          "Because job costing always involves larger sums of money",
          "There is no difference in how costs are tracked between the two methods"
        ],
        "a": 0,
        "e": "Since contract costing deals with a single large, identifiable project, its costs are directly linked to that specific contract rather than being shared across multiple smaller jobs as in job costing.",
        "h": "Recall the comparison table's point about cost identification.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Departmental Account",
    "topicCode": "SS3-ACC-05",
    "module": "Departmental and Branch Accounts",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">departmental account</span> is prepared when a single business is organised into separate departments (e.g., a store with a clothing department, an electronics department, and a groceries department), allowing the profitability of each department to be assessed individually.\n</div>\n\n<h3 class=\"learn-subheading\">1. Purpose of Departmental Accounts</h3>\n<ul class=\"learn-list\">\n  <li>To determine the profit or loss of each individual department separately</li>\n  <li>To help management identify which departments are performing well and which are underperforming</li>\n  <li>To support decisions such as expanding, reducing, or closing a particular department</li>\n  <li>To evaluate the efficiency of departmental managers and staff</li>\n</ul>\n\n<h3 class=\"learn-subheading\">2. Allocation vs. Apportionment of Expenses</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Term</th><th>Meaning</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Allocation</td><td>Charging an expense entirely to one specific department, because it relates solely to that department</td><td>Salary of a department's own dedicated sales staff</td></tr>\n      <tr><td>Apportionment</td><td>Sharing a common expense across multiple departments using a fair basis, since it cannot be traced to one department alone</td><td>Rent, shared based on floor space occupied by each department</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Common Bases of Apportionment</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Expense</th><th>Common Basis of Apportionment</th></tr></thead>\n    <tbody>\n      <tr><td>Rent and rates</td><td>Floor space occupied by each department</td></tr>\n      <tr><td>Lighting expenses</td><td>Floor space or number of light points in each department</td></tr>\n      <tr><td>Insurance of stock</td><td>Value of stock held by each department</td></tr>\n      <tr><td>General manager's salary</td><td>Time spent on each department, or an agreed fixed ratio</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Preparing a Departmental Trading, Profit and Loss Account</h3>\n<p class=\"learn-p\">Each department's sales, cost of sales, and gross profit are calculated in separate columns. Directly allocable expenses are charged to the specific department, while common expenses are apportioned using a fair basis, to arrive at each department's net profit.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> \"Allocation\" is for costs that belong wholly to ONE department (no calculation needed to split it), while \"apportionment\" is for shared costs that must be divided using a fair basis like floor space or stock value.\n</div>\n",
    "questions": [
      {
        "q": "A 'departmental account' is prepared mainly to:",
        "o": [
          "Combine all departments into a single, undifferentiated profit figure with no breakdown",
          "Assess the profitability of each individual department within a single business separately",
          "Replace the need for a full company balance sheet",
          "Record only the cash transactions of one department"
        ],
        "a": 1,
        "e": "Departmental accounts are prepared specifically to assess the profitability of each individual department within a business separately.",
        "h": "Focus on the purpose of separating out performance by department.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a stated purpose of departmental accounts?",
        "o": [
          "To help management identify which departments are performing well and which are underperforming",
          "To eliminate the need for any financial records",
          "To merge all departments into one indistinguishable unit",
          "To avoid calculating any profit figures at all"
        ],
        "a": 0,
        "e": "Departmental accounts help management identify which departments are performing well and which are underperforming, supporting better business decisions.",
        "h": "Recall the purposes listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "'Allocation' of an expense in departmental accounting means:",
        "o": [
          "Sharing the expense across multiple departments using a fair basis",
          "Charging an expense entirely to one specific department because it relates solely to that department",
          "Ignoring the expense completely",
          "Charging the expense to the government"
        ],
        "a": 1,
        "e": "Allocation means charging an expense entirely to a specific department, since that expense relates solely to that department and can be directly traced to it.",
        "h": "Recall the definition of 'allocation' given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "'Apportionment' of an expense in departmental accounting means:",
        "o": [
          "Charging an expense entirely to one department only",
          "Sharing a common expense across multiple departments using a fair basis",
          "Removing the expense from all financial records",
          "Paying the expense directly to a bank"
        ],
        "a": 1,
        "e": "Apportionment means sharing a common expense, which cannot be traced to one department alone, across multiple departments using a fair basis.",
        "h": "Recall the definition of 'apportionment' given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is an example of an expense that would typically be ALLOCATED rather than apportioned?",
        "o": [
          "General building rent for the whole store",
          "Salary of a department's own dedicated sales staff",
          "Overall building insurance",
          "General manager's salary covering all departments"
        ],
        "a": 1,
        "e": "The salary of a department's own dedicated sales staff relates solely to that department and would typically be allocated entirely to it, rather than apportioned.",
        "h": "Look for the expense that clearly belongs to just one department.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following is a common basis for apportioning rent and rates among departments?",
        "o": [
          "Number of employees only, regardless of space used",
          "Floor space occupied by each department",
          "Total sales value only",
          "Random guesswork"
        ],
        "a": 1,
        "e": "Rent and rates are commonly apportioned based on the floor space occupied by each department, since this reflects fair usage of the rented premises.",
        "h": "Recall the common basis listed in the table for rent and rates.",
        "yr": "SS3"
      },
      {
        "q": "Insurance of stock is typically apportioned based on:",
        "o": [
          "The value of stock held by each department",
          "The number of staff in each department",
          "The floor space of the building's exterior",
          "A fixed amount unrelated to stock value"
        ],
        "a": 0,
        "e": "Insurance of stock is typically apportioned based on the value of stock held by each department, since higher-value stock generally requires more insurance coverage.",
        "h": "Recall the specific basis listed for insurance of stock.",
        "yr": "SS3"
      },
      {
        "q": "Lighting expenses are commonly apportioned based on:",
        "o": [
          "Floor space or number of light points in each department",
          "The department manager's personal preference",
          "Total company revenue only",
          "The number of customers served"
        ],
        "a": 0,
        "e": "Lighting expenses are commonly apportioned based on floor space or the number of light points present in each department.",
        "h": "Recall the basis listed for lighting expenses in the table.",
        "yr": "SS3"
      },
      {
        "q": "The general manager's salary, covering oversight of all departments, is often apportioned based on:",
        "o": [
          "Time spent on each department, or an agreed fixed ratio",
          "The exact floor space of the manager's own office only",
          "Random selection",
          "The manager's personal preference alone with no formal basis"
        ],
        "a": 0,
        "e": "The general manager's salary is often apportioned based on the time spent overseeing each department, or according to an agreed fixed ratio.",
        "h": "Recall the specific basis listed for the general manager's salary.",
        "yr": "SS3"
      },
      {
        "q": "Why can't a shared expense like general rent be simply allocated to one department?",
        "o": [
          "Because it cannot be traced to one department alone, since it benefits multiple departments jointly",
          "Because rent is never a real business expense",
          "Because allocation is illegal for any expense",
          "Because rent is always zero in departmental accounting"
        ],
        "a": 0,
        "e": "Shared expenses like general rent benefit multiple departments jointly and cannot be traced to just one department, so they must be apportioned using a fair basis instead of being allocated.",
        "h": "Recall the exam tip distinguishing allocation from apportionment.",
        "yr": "SS3"
      },
      {
        "q": "In preparing a departmental trading, profit and loss account, each department's sales and cost of sales are:",
        "o": [
          "Combined into a single figure with no breakdown at all",
          "Calculated in separate columns for each department",
          "Ignored completely in the account",
          "Only recorded for the best-performing department"
        ],
        "a": 1,
        "e": "Each department's sales, cost of sales, and gross profit are calculated and presented in separate columns to allow individual assessment.",
        "h": "Recall the format described for preparing this type of account.",
        "yr": "SS3"
      },
      {
        "q": "Departmental accounts can support which type of business decision?",
        "o": [
          "Whether to expand, reduce, or close a particular department",
          "Only decisions about which country to operate in",
          "Decisions completely unrelated to business performance",
          "Decisions about unrelated personal matters of the owner"
        ],
        "a": 0,
        "e": "Departmental accounts provide the profitability information needed to support decisions such as expanding, reducing, or closing a particular underperforming or overperforming department.",
        "h": "Recall the specific business decision purpose listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "If a store has three departments occupying floor space in the ratio 2:3:5, and total rent is ₦100,000, how much rent would be apportioned to the department with the ratio '3'?",
        "o": [
          "₦20,000",
          "₦30,000",
          "₦50,000",
          "₦100,000"
        ],
        "a": 1,
        "e": "Total ratio parts = 2+3+5 = 10. The department with ratio 3 gets (3/10) × ₦100,000 = ₦30,000.",
        "h": "Divide the ratio value by the total ratio sum, then multiply by total rent.",
        "yr": "SS3"
      },
      {
        "q": "Using the same example, how much rent would be apportioned to the department with the ratio '5'?",
        "o": [
          "₦20,000",
          "₦30,000",
          "₦50,000",
          "₦100,000"
        ],
        "a": 2,
        "e": "(5/10) × ₦100,000 = ₦50,000, apportioned to the department with the largest floor space ratio.",
        "h": "Apply the same ratio calculation method as before.",
        "yr": "SS3"
      },
      {
        "q": "Departmental accounting helps evaluate:",
        "o": [
          "The efficiency of departmental managers and staff",
          "Only the performance of the company's competitors",
          "The personal finances of the business owner unrelated to the business",
          "Nothing useful to management"
        ],
        "a": 0,
        "e": "Departmental accounts help evaluate the efficiency and performance of departmental managers and their staff, based on the individual profitability figures produced.",
        "h": "Recall the management evaluation purpose listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following expenses would most likely need to be apportioned rather than directly allocated?",
        "o": [
          "A specific department's dedicated delivery van running costs",
          "Overall building insurance covering the entire store",
          "Wages of staff working exclusively in one department",
          "Cost of goods purchased specifically for one department"
        ],
        "a": 1,
        "e": "Overall building insurance covers the entire store and benefits all departments jointly, so it must be apportioned using a fair basis rather than allocated to a single department.",
        "h": "Look for the expense that clearly benefits the whole business, not just one department.",
        "yr": "SS3"
      },
      {
        "q": "Why is it important to use a 'fair basis' when apportioning expenses among departments?",
        "o": [
          "To ensure each department bears a reasonable, justifiable share of the shared cost, reflecting its actual usage or benefit",
          "Because fairness has no real importance in accounting",
          "To ensure only the largest department bears all costs, regardless of usage",
          "Because apportionment bases are chosen randomly with no justification needed"
        ],
        "a": 0,
        "e": "Using a fair basis ensures each department bears a reasonable, justifiable share of a shared cost that reflects its actual usage or benefit, leading to accurate departmental profit figures.",
        "h": "Think about what makes an apportionment method 'fair' and useful.",
        "yr": "SS3"
      },
      {
        "q": "A department showing consistently poor profit figures compared to others, as revealed through departmental accounts, might lead management to:",
        "o": [
          "Ignore the department's performance completely",
          "Investigate the causes and consider actions such as restructuring or closing that department",
          "Automatically close the entire business",
          "Immediately fire all staff company-wide with no investigation"
        ],
        "a": 1,
        "e": "Consistently poor departmental performance revealed through departmental accounts would typically lead management to investigate the causes and consider appropriate actions, such as restructuring or closing that specific department.",
        "h": "Think about a reasonable, measured management response to this information.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following best summarises the key difference between allocation and apportionment?",
        "o": [
          "Allocation charges an expense wholly to one department; apportionment divides a shared expense among departments using a fair basis",
          "They are exactly the same accounting process",
          "Apportionment always results in a larger total expense than allocation",
          "Allocation is only used for revenue, never expenses"
        ],
        "a": 0,
        "e": "Allocation charges an expense entirely to one department because it belongs solely to that department, while apportionment divides a shared expense among multiple departments based on a fair basis.",
        "h": "Recall the exam tip's core distinction between the two terms.",
        "yr": "SS3"
      },
      {
        "q": "Why might a business choose to organise itself into separate departments with individual accounts, rather than treating the whole business as one unit?",
        "o": [
          "To gain detailed insight into which specific business areas are profitable, supporting better-informed management decisions",
          "Because departmental accounting is a legal requirement for every business worldwide",
          "Because it eliminates the need for any overall company accounts",
          "Because it makes bookkeeping simpler by combining all data into one figure"
        ],
        "a": 0,
        "e": "Organising a business into departments with individual accounts provides detailed insight into which specific business areas are profitable, enabling better-informed management decisions about resource allocation.",
        "h": "Think about the overall business benefit of this detailed, department-level insight.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Branch Account",
    "topicCode": "SS3-ACC-06",
    "module": "Departmental and Branch Accounts",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  A <span class=\"learn-keyword\">branch account</span> is prepared by a business (head office) that operates one or more branches in different locations, to track and assess each branch's performance and its financial relationship with head office.\n</div>\n\n<h3 class=\"learn-subheading\">1. Types of Branches</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Dependent branch</td><td>Relies entirely on head office for goods, and typically does not keep a full, independent set of accounting books</td></tr>\n      <tr><td>Independent branch</td><td>Operates with more autonomy, keeps its own complete set of books, and may purchase some goods locally, submitting periodic accounts to head office</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">2. Systems of Branch Accounting (for Dependent Branches)</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>System</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Debtors system</td><td>The branch is treated like a debtor of head office; a single Branch Account records goods sent, cash remitted, expenses, and closing balances to determine branch profit</td></tr>\n      <tr><td>Stock and debtors system</td><td>Separates the memorandum stock account (tracking goods movement in quantity or at selling price) from the debtors account and adjustment account, giving more detailed control over branch stock</td></tr>\n      <tr><td>Final accounts system</td><td>A full trading, profit and loss account and balance sheet are prepared for the branch, similar to preparing accounts for an entire separate business</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. The Debtors System in Detail</h3>\n<p class=\"learn-p\">Under the debtors system, the Branch Account is debited with: opening balances (stock, debtors, cash), goods sent to branch, and any expenses paid by head office on the branch's behalf. It is credited with: cash remitted by the branch to head office, goods returned by the branch, and closing balances (stock, debtors, cash) at period end. The balancing figure represents the branch's profit or loss for the period.</p>\n\n<h3 class=\"learn-subheading\">4. Why Branch Accounting Matters</h3>\n<p class=\"learn-p\">Branch accounts allow head office to monitor the profitability and financial health of each branch individually, detect discrepancies (such as stock shortages or unauthorised discounts), and make informed decisions about expanding, supporting, or closing specific branches.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Under the debtors system, remember: closing balances (stock, debtors, cash) go on the CREDIT side of the Branch Account, since they represent value still held by the branch at period end, not yet returned to head office as profit.\n</div>\n",
    "questions": [
      {
        "q": "A 'dependent branch' is best described as:",
        "o": [
          "A branch that operates with full independence and its own complete books",
          "A branch that relies entirely on head office for goods and typically does not keep a full, independent set of books",
          "A branch located only overseas",
          "A branch that has no connection to head office"
        ],
        "a": 1,
        "e": "A dependent branch relies entirely on head office for goods and typically does not maintain a full, independent set of accounting books.",
        "h": "Focus on the word 'dependent' — relying on head office.",
        "yr": "SS3"
      },
      {
        "q": "An 'independent branch' is best described as:",
        "o": [
          "A branch with no relationship to head office whatsoever",
          "A branch that operates with more autonomy, keeps its own complete set of books, and submits periodic accounts to head office",
          "A branch that never purchases any goods",
          "A branch that is entirely owned by a different company"
        ],
        "a": 1,
        "e": "An independent branch operates with more autonomy, maintains its own complete set of books, and periodically submits accounts to head office.",
        "h": "Focus on the greater autonomy and own bookkeeping this type of branch has.",
        "yr": "SS3"
      },
      {
        "q": "Under the 'debtors system' of branch accounting, the branch is treated like:",
        "o": [
          "A completely separate, unrelated company",
          "A debtor of head office",
          "A creditor of head office",
          "A government agency"
        ],
        "a": 1,
        "e": "Under the debtors system, the branch is treated like a debtor of head office, with a single Branch Account tracking the relationship.",
        "h": "Recall the name of this system and what it implies about the branch's status.",
        "yr": "SS3"
      },
      {
        "q": "Which system of branch accounting separates a memorandum stock account from the debtors account and adjustment account?",
        "o": [
          "Debtors system",
          "Stock and debtors system",
          "Final accounts system",
          "None of these systems"
        ],
        "a": 1,
        "e": "The stock and debtors system specifically separates a memorandum stock account (tracking goods movement) from the debtors and adjustment accounts, providing more detailed stock control.",
        "h": "Recall the system named specifically for this separation.",
        "yr": "SS3"
      },
      {
        "q": "Which system of branch accounting involves preparing a full trading, profit and loss account and balance sheet for the branch, similar to a separate business?",
        "o": [
          "Debtors system",
          "Stock and debtors system",
          "Final accounts system",
          "None of these systems"
        ],
        "a": 2,
        "e": "The final accounts system involves preparing a full trading, profit and loss account and balance sheet for the branch, much like an entirely separate business.",
        "h": "Recall the system named after preparing full 'final accounts'.",
        "yr": "SS3"
      },
      {
        "q": "Under the debtors system, the Branch Account is DEBITED with:",
        "o": [
          "Cash remitted by the branch to head office",
          "Opening balances, goods sent to branch, and expenses paid by head office on the branch's behalf",
          "Closing balances of stock, debtors, and cash",
          "Goods returned by the branch"
        ],
        "a": 1,
        "e": "The Branch Account is debited with opening balances, goods sent to the branch, and any expenses paid by head office on the branch's behalf.",
        "h": "Recall the debit-side items listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Under the debtors system, the Branch Account is CREDITED with:",
        "o": [
          "Goods sent to the branch",
          "Opening balances of stock and debtors",
          "Cash remitted by the branch, goods returned, and closing balances at period end",
          "Expenses paid by head office"
        ],
        "a": 2,
        "e": "The Branch Account is credited with cash remitted by the branch to head office, any goods returned by the branch, and the closing balances at the end of the period.",
        "h": "Recall the credit-side items listed in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "In the debtors system, the balancing figure in the Branch Account represents:",
        "o": [
          "The total contract price",
          "The branch's profit or loss for the period",
          "The head office's unrelated expenses",
          "The retention money withheld"
        ],
        "a": 1,
        "e": "The balancing figure in the Branch Account under the debtors system represents the branch's profit or loss for the accounting period.",
        "h": "Recall what the final balancing amount signifies in this account.",
        "yr": "SS3"
      },
      {
        "q": "Why are closing balances (stock, debtors, cash) placed on the CREDIT side of the Branch Account?",
        "o": [
          "Because they represent value still held by the branch at period end, not yet returned to head office as realised profit",
          "Because closing balances are always zero",
          "Because this placement has no accounting logic",
          "Because they represent goods sent by head office"
        ],
        "a": 0,
        "e": "Closing balances go on the credit side because they represent value still held by the branch at period end, which offsets the debit-side entries when calculating branch profit.",
        "h": "Recall the exam tip explaining this specific placement.",
        "yr": "SS3"
      },
      {
        "q": "Which type of branch is more likely to purchase some of its goods locally, rather than relying solely on head office?",
        "o": [
          "Dependent branch",
          "Independent branch",
          "Neither type ever purchases locally",
          "Both types are identical in this respect"
        ],
        "a": 1,
        "e": "An independent branch, given its greater autonomy, may purchase some goods locally rather than relying entirely on goods sent from head office.",
        "h": "Recall the feature distinguishing independent branches from dependent ones.",
        "yr": "SS3"
      },
      {
        "q": "Branch accounting allows head office to:",
        "o": [
          "Ignore the performance of individual branches entirely",
          "Monitor the profitability and financial health of each branch individually",
          "Avoid preparing any financial records for branches",
          "Eliminate the need for head office's own accounts"
        ],
        "a": 1,
        "e": "Branch accounting allows head office to monitor the profitability and financial health of each individual branch, supporting informed management decisions.",
        "h": "Recall the stated importance of branch accounting in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Branch accounts can help head office detect:",
        "o": [
          "Discrepancies such as stock shortages or unauthorised discounts",
          "Only unrelated national economic trends",
          "Nothing useful about branch operations",
          "Only head office's own internal errors, never branch issues"
        ],
        "a": 0,
        "e": "Branch accounts can help head office detect discrepancies such as stock shortages or unauthorised discounts occurring at a specific branch.",
        "h": "Recall the specific type of issue branch accounting can help reveal.",
        "yr": "SS3"
      },
      {
        "q": "Which system of branch accounting would likely be used for a large, established branch that operates almost like a separate business, with its own bank account and detailed local records?",
        "o": [
          "Debtors system",
          "Stock and debtors system",
          "Final accounts system",
          "No system would apply"
        ],
        "a": 2,
        "e": "The final accounts system, involving a full trading, profit and loss account and balance sheet, would suit a large, established branch operating almost like a separate business.",
        "h": "Recall which system mirrors preparing accounts for an entire separate business.",
        "yr": "SS3"
      },
      {
        "q": "If a dependent branch's opening stock was ₦20,000, goods sent during the year were ₦150,000, and closing stock was ₦30,000, what would be the approximate cost of goods available for sale calculation contribution from these figures (opening stock + goods sent)?",
        "o": [
          "₦150,000",
          "₦170,000",
          "₦180,000",
          "₦200,000"
        ],
        "a": 1,
        "e": "Opening stock (₦20,000) + goods sent (₦150,000) = ₦170,000, representing goods available before considering the closing stock.",
        "h": "Simply add the opening stock and goods sent figures together.",
        "yr": "SS3"
      },
      {
        "q": "Why does head office typically prefer the debtors system for a small, simple dependent branch rather than the final accounts system?",
        "o": [
          "Because the debtors system is simpler and sufficient when detailed independent record-keeping is not required at the branch level",
          "Because the debtors system requires more paperwork than the final accounts system",
          "Because the final accounts system is illegal for small branches",
          "Because there is no real difference in complexity between the two systems"
        ],
        "a": 0,
        "e": "For a small, simple dependent branch, the debtors system is generally simpler and sufficient, since detailed independent record-keeping (as required in the final accounts system) is not necessary.",
        "h": "Think about matching the complexity of the accounting system to the branch's size and needs.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would be classified as an expense paid by head office on behalf of a dependent branch, appearing on the debit side of the Branch Account?",
        "o": [
          "Rent for the branch premises, paid directly by head office",
          "Cash remitted by the branch to head office",
          "Closing stock at the branch",
          "Goods returned by the branch to head office"
        ],
        "a": 0,
        "e": "Rent for the branch premises, if paid directly by head office on the branch's behalf, would be debited to the Branch Account as an expense incurred for the branch.",
        "h": "Look for the option representing an expense paid by head office, not a value returned by the branch.",
        "yr": "SS3"
      },
      {
        "q": "A key advantage of the 'stock and debtors system' over the simpler 'debtors system' is that it provides:",
        "o": [
          "Less detailed information about branch stock movement",
          "More detailed control and tracking of branch stock movement, separate from debtor and cash tracking",
          "No information about branch stock at all",
          "Identical information to the debtors system, with no added benefit"
        ],
        "a": 1,
        "e": "The stock and debtors system provides more detailed control and tracking of branch stock movement, by separating this from the debtors and cash tracking found in the simpler debtors system.",
        "h": "Recall the specific benefit of separating stock tracking, as described in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Which type of branch would be LESS likely to require detailed local bookkeeping staff, since head office manages most records?",
        "o": [
          "Independent branch",
          "Dependent branch",
          "Both types require identical bookkeeping staff",
          "Neither type requires any records"
        ],
        "a": 1,
        "e": "A dependent branch, relying heavily on head office and not keeping a full independent set of books, would be less likely to require detailed local bookkeeping staff.",
        "h": "Recall which branch type relies more heavily on head office for record-keeping.",
        "yr": "SS3"
      },
      {
        "q": "Periodic submission of accounts to head office is a feature most associated with:",
        "o": [
          "Dependent branches only, since they have no accounts of their own",
          "Independent branches, which maintain their own books and report back to head office periodically",
          "Neither type of branch ever reports to head office",
          "Only head office itself, with no branch reporting involved"
        ],
        "a": 1,
        "e": "Independent branches, since they maintain their own complete set of books, are the ones that periodically submit accounts back to head office for consolidation.",
        "h": "Recall the description of independent branches given in the lesson.",
        "yr": "SS3"
      },
      {
        "q": "Overall, why is understanding different branch accounting systems important for a business with multiple branch locations?",
        "o": [
          "It has no real importance to multi-branch businesses",
          "It allows the business to choose an appropriate accounting approach based on each branch's size, autonomy, and record-keeping needs",
          "It forces every branch to use the exact same accounting system regardless of circumstances",
          "It eliminates the need for head office to track branch performance at all"
        ],
        "a": 1,
        "e": "Understanding different branch accounting systems allows a multi-branch business to choose an appropriate approach for each branch, based on its size, level of autonomy, and record-keeping needs.",
        "h": "Think about the practical value of having multiple systems to choose from.",
        "yr": "SS3"
      }
    ]
  },
  {
    "topic": "Public Sector Accounting",
    "topicCode": "SS3-ACC-07",
    "module": "Public Sector Accounting",
    "term": "First Term",
    "contentHTML": "\n<div class=\"learn-intro\">\n  <span class=\"learn-keyword\">Public sector accounting</span> deals with recording and reporting the financial transactions of government bodies (federal, state, and local governments), which differs in objective and structure from private sector (business) accounting.\n</div>\n\n<h3 class=\"learn-subheading\">1. Meaning and Objectives</h3>\n<p class=\"learn-p\">Public sector accounting is concerned with the collection, classification, and reporting of financial transactions relating to government activities. Its primary objectives are accountability and stewardship over public funds, ensuring resources are used for the public good, rather than the profit-driven objectives of private businesses.</p>\n\n<h3 class=\"learn-subheading\">2. Sources of Government Revenue</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Source</th><th>Example</th></tr></thead>\n    <tbody>\n      <tr><td>Taxation</td><td>Personal income tax, company income tax, value-added tax (VAT)</td></tr>\n      <tr><td>Non-tax revenue</td><td>Fees, fines, licences, and royalties</td></tr>\n      <tr><td>Grants and aid</td><td>Funds received from other governments or international organisations</td></tr>\n      <tr><td>Loans</td><td>Borrowing from domestic or international sources</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">3. Types of Government Expenditure</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Type</th><th>Description</th></tr></thead>\n    <tbody>\n      <tr><td>Recurrent expenditure</td><td>Regular, day-to-day spending (salaries, maintenance, running costs) that recurs every budget period</td></tr>\n      <tr><td>Capital expenditure</td><td>Spending on long-term infrastructure and development projects (roads, hospitals, schools) that yields benefits over many years</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">4. Public Sector vs. Private Sector Accounting</h3>\n<div class=\"learn-table-wrap\">\n  <table class=\"learn-table\">\n    <thead><tr><th>Public Sector Accounting</th><th>Private Sector Accounting</th></tr></thead>\n    <tbody>\n      <tr><td>Primary aim: accountability and public service, not profit</td><td>Primary aim: profit maximisation for owners/shareholders</td></tr>\n      <tr><td>Funded mainly by taxation, grants, and loans</td><td>Funded mainly by owners' capital, loans, and trading revenue</td></tr>\n      <tr><td>Uses \"fund accounting\" concepts (e.g., Consolidated Revenue Fund)</td><td>Uses standard financial statements (trading, profit and loss, balance sheet)</td></tr>\n      <tr><td>Budget-focused, following government fiscal year and approved estimates</td><td>Accounting period may vary; not tied to a national budget cycle</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class=\"learn-subheading\">5. Structure of Government Accounting</h3>\n<p class=\"learn-p\">Government accounts are typically structured around the annual budget, with revenue paid into a Consolidated Revenue Fund, and expenditure authorised through an approved appropriation (budget) act, ensuring funds are spent only as approved by the legislature.</p>\n\n<div class=\"learn-tip-box\">\n  <span class=\"learn-tip-icon\">💡</span>\n  <strong>Exam Tip:</strong> Don't confuse \"recurrent expenditure\" (regular, repeating costs like salaries) with \"capital expenditure\" (one-off, long-term investment like building a hospital) — WAEC frequently tests this classification.\n</div>\n",
    "questions": [
      {
        "q": "Public sector accounting is primarily concerned with:",
        "o": [
          "Maximising profit for private shareholders",
          "The collection, classification, and reporting of financial transactions relating to government activities",
          "Only recording personal household expenses",
          "Managing a single private company's stock"
        ],
        "a": 1,
        "e": "Public sector accounting deals with the collection, classification, and reporting of financial transactions relating to government activities.",
        "h": "Focus on the government-related scope of this branch of accounting.",
        "yr": "SS3"
      },
      {
        "q": "The primary objective of public sector accounting is:",
        "o": [
          "Profit maximisation for shareholders",
          "Accountability and stewardship over public funds, ensuring resources are used for the public good",
          "Avoiding all financial record-keeping",
          "Maximising government debt"
        ],
        "a": 1,
        "e": "Public sector accounting's primary objective is accountability and stewardship over public funds, ensuring they are used appropriately for the public good.",
        "h": "Recall the key objectives stated in the lesson's introduction.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a source of TAX revenue for government?",
        "o": [
          "Personal income tax",
          "Grants from international organisations",
          "Loans from domestic sources",
          "Licence fees"
        ],
        "a": 0,
        "e": "Personal income tax is a direct example of tax revenue, unlike grants, loans, or licence fees, which fall under other revenue categories.",
        "h": "Look for the option specifically labelled as a type of tax.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is an example of NON-TAX revenue for government?",
        "o": [
          "Company income tax",
          "Value-added tax (VAT)",
          "Fees, fines, and licences",
          "Personal income tax"
        ],
        "a": 2,
        "e": "Fees, fines, and licences are examples of non-tax revenue, as distinguished from direct and indirect taxes like income tax and VAT.",
        "h": "Look for the option that is not a form of direct taxation.",
        "yr": "SS3"
      },
      {
        "q": "Funds received from other governments or international organisations are classified as:",
        "o": [
          "Taxation",
          "Grants and aid",
          "Recurrent expenditure",
          "Capital expenditure"
        ],
        "a": 1,
        "e": "Funds received from other governments or international organisations are classified specifically as grants and aid, a distinct government revenue source.",
        "h": "Recall the specific revenue category for such external funding.",
        "yr": "SS3"
      },
      {
        "q": "'Recurrent expenditure' refers to:",
        "o": [
          "One-off spending on long-term infrastructure projects",
          "Regular, day-to-day spending, such as salaries and maintenance, that recurs every budget period",
          "Money received from taxation only",
          "Loans taken by the government"
        ],
        "a": 1,
        "e": "Recurrent expenditure refers to regular, day-to-day government spending, such as salaries and maintenance, that recurs every budget period.",
        "h": "Think of the word 'recurrent' meaning happening repeatedly.",
        "yr": "SS3"
      },
      {
        "q": "'Capital expenditure' refers to:",
        "o": [
          "Regular monthly salary payments only",
          "Spending on long-term infrastructure and development projects, such as roads and hospitals",
          "A type of tax revenue",
          "Only expenditure on office stationery"
        ],
        "a": 1,
        "e": "Capital expenditure refers to spending on long-term infrastructure and development projects, like roads, hospitals, and schools, which yield benefits over many years.",
        "h": "Think of major long-term investments, not everyday running costs.",
        "yr": "SS3"
      },
      {
        "q": "Which of these would be classified as recurrent expenditure?",
        "o": [
          "Building a new hospital",
          "Monthly salaries paid to civil servants",
          "Constructing a new highway",
          "Purchasing land for a future project"
        ],
        "a": 1,
        "e": "Monthly salaries paid to civil servants are a regular, repeating cost, making them a clear example of recurrent expenditure.",
        "h": "Look for the option representing a regular, repeating cost.",
        "yr": "SS3"
      },
      {
        "q": "Which of these would be classified as capital expenditure?",
        "o": [
          "Monthly electricity bills for government offices",
          "Constructing a new highway",
          "Salaries of teachers",
          "Office stationery purchases"
        ],
        "a": 1,
        "e": "Constructing a new highway is a long-term infrastructure investment, making it a clear example of capital expenditure.",
        "h": "Look for the option representing a major, long-term investment project.",
        "yr": "SS3"
      },
      {
        "q": "Which best distinguishes public sector accounting's primary aim from private sector accounting's primary aim?",
        "o": [
          "Public sector aims for accountability and public service; private sector aims for profit maximisation",
          "They both aim exclusively at profit maximisation",
          "Public sector accounting has no defined aim at all",
          "Private sector accounting is only concerned with government spending"
        ],
        "a": 0,
        "e": "Public sector accounting's primary aim is accountability and public service, while private sector accounting's primary aim is profit maximisation for owners or shareholders.",
        "h": "Recall the comparison table's first point of distinction.",
        "yr": "SS3"
      },
      {
        "q": "Public sector accounting is mainly funded by:",
        "o": [
          "Owners' personal capital contributions only",
          "Taxation, grants, and loans",
          "Only trading revenue from selling goods",
          "Shareholder dividends"
        ],
        "a": 1,
        "e": "Public sector accounting activities are mainly funded through taxation, grants, and loans, unlike private businesses which rely on owners' capital and trading revenue.",
        "h": "Recall the funding sources listed in the comparison table for the public sector.",
        "yr": "SS3"
      },
      {
        "q": "The 'Consolidated Revenue Fund' is best understood as:",
        "o": [
          "A private company's savings account",
          "The central fund into which government revenue is paid, from which approved expenditure is authorised",
          "A fund used exclusively by private businesses",
          "A type of personal bank account"
        ],
        "a": 1,
        "e": "The Consolidated Revenue Fund is the central government fund into which revenue is paid and from which expenditure is authorised through an approved budget.",
        "h": "Recall the description of this fund in the 'Structure of Government Accounting' section.",
        "yr": "SS3"
      },
      {
        "q": "Government expenditure is authorised through:",
        "o": [
          "An approved appropriation (budget) act, ensuring spending matches legislative approval",
          "Random decisions with no formal approval process",
          "Only the president's personal discretion, with no other oversight",
          "Private company board meetings"
        ],
        "a": 0,
        "e": "Government expenditure is authorised through an approved appropriation (budget) act, ensuring funds are spent only as approved by the legislature.",
        "h": "Recall the formal process described for authorising government spending.",
        "yr": "SS3"
      },
      {
        "q": "Which of these is a key structural feature of government accounting, distinguishing it from private sector accounting?",
        "o": [
          "It is budget-focused, following the government's fiscal year and approved estimates",
          "It uses no budget or fiscal year concept at all",
          "It is identical in every respect to private company accounting",
          "It never involves any legislative oversight"
        ],
        "a": 0,
        "e": "Government accounting is distinctly budget-focused, following the government's fiscal year and approved budget estimates, unlike private sector accounting which may not follow this cycle.",
        "h": "Recall the comparison table's point about budget focus.",
        "yr": "SS3"
      },
      {
        "q": "Which financial statement approach is typically used in PRIVATE sector accounting, as opposed to fund accounting concepts?",
        "o": [
          "Consolidated Revenue Fund reporting only",
          "Standard financial statements such as trading, profit and loss, and balance sheet",
          "No financial statements at all",
          "Only government appropriation acts"
        ],
        "a": 1,
        "e": "Private sector accounting typically uses standard financial statements such as trading accounts, profit and loss accounts, and balance sheets, unlike the fund accounting concepts used in the public sector.",
        "h": "Recall which approach belongs to private businesses in the comparison table.",
        "yr": "SS3"
      },
      {
        "q": "Why is 'accountability' emphasised as a key objective in public sector accounting?",
        "o": [
          "Because public funds belong to citizens collectively, and government must demonstrate responsible use of these resources",
          "Because accountability has no real relevance to government operations",
          "Because government accounts are always kept secret from the public",
          "Because private profit is the sole measure of government success"
        ],
        "a": 0,
        "e": "Accountability is emphasised because public funds ultimately belong to citizens collectively, and government has a duty to demonstrate responsible, transparent use of these resources.",
        "h": "Think about who ultimately owns and is affected by the use of public funds.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following best represents the difference in fundamental purpose between public and private sector organisations, as reflected in their accounting?",
        "o": [
          "Public sector organisations exist to serve public interests; private sector organisations exist primarily to generate profit for owners",
          "Both sectors exist solely to generate maximum profit",
          "Public sector organisations have no defined purpose at all",
          "Private sector organisations exist solely to serve the public with no profit motive"
        ],
        "a": 0,
        "e": "Public sector organisations primarily exist to serve public interests and needs, while private sector organisations primarily exist to generate profit for their owners or shareholders — a difference reflected in their respective accounting approaches.",
        "h": "Recall the fundamental purpose distinction discussed throughout the lesson.",
        "yr": "SS3"
      },
      {
        "q": "A new government hospital building project would be classified in the government budget as:",
        "o": [
          "Recurrent expenditure",
          "Capital expenditure",
          "Non-tax revenue",
          "Taxation"
        ],
        "a": 1,
        "e": "Building a new hospital is a long-term infrastructure investment, correctly classified as capital expenditure rather than a recurring operational cost.",
        "h": "Think about the long-term, one-off nature of this specific project.",
        "yr": "SS3"
      },
      {
        "q": "Which of the following would be an example of government revenue derived from BORROWING, rather than taxation?",
        "o": [
          "Value-added tax (VAT) collections",
          "A loan obtained from an international financial institution",
          "Personal income tax collections",
          "Licence fee payments"
        ],
        "a": 1,
        "e": "A loan obtained from an international financial institution represents government revenue derived from borrowing, distinct from tax-based revenue sources like VAT or income tax.",
        "h": "Look for the option specifically describing borrowed funds.",
        "yr": "SS3"
      },
      {
        "q": "Overall, why is understanding public sector accounting important for citizens and policymakers?",
        "o": [
          "It has no real importance to ordinary citizens or policymakers",
          "It helps ensure transparency, accountability, and effective use of public resources for national development",
          "It only matters to private business owners, not to the government or citizens",
          "It is solely a theoretical topic with no practical, real-world application"
        ],
        "a": 1,
        "e": "Understanding public sector accounting is important because it helps ensure transparency, accountability, and the effective use of public resources, ultimately supporting national development.",
        "h": "Recall the broader significance framed at the start of the lesson regarding accountability.",
        "yr": "SS3"
      }
    ]
  }
];
